/**
 * useUrlConfig Hook
 *
 * Parses widget configuration from URL parameters
 *
 * URL format: /embed/{venueId}?apiKey=pk_xxx&primaryColor=%23FF0000&position=bottom-right
 */

import { useMemo } from "react";
import type {
  ConciergeWidgetProps,
  WidgetPosition,
} from "@chekmenu/concierge-core";

interface UrlConfig {
  venueId: string;
  apiKey: string;
  primaryColor?: string;
  accentColor?: string;
  position: WidgetPosition;
  welcomeDelay: number;
  baseUrl: string;
  isValid: boolean;
  error?: string;
}

/**
 * Parse URL parameters into widget config
 */
export function useUrlConfig(): UrlConfig {
  return useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;

    // Extract venueId from path: /embed/{venueId}
    const pathParts = pathname.split("/").filter(Boolean);
    const embedIndex = pathParts.indexOf("embed");
    const venueId = embedIndex >= 0 ? pathParts[embedIndex + 1] : "";

    // Get required params
    const apiKey = params.get("apiKey") || "";

    // Validate required fields
    if (!venueId) {
      return {
        venueId: "",
        apiKey: "",
        position: "bottom-right",
        welcomeDelay: 2500,
        baseUrl: "",
        isValid: false,
        error: "Missing venueId in URL path. Expected format: /embed/{venueId}",
      };
    }

    if (!apiKey) {
      return {
        venueId,
        apiKey: "",
        position: "bottom-right",
        welcomeDelay: 2500,
        baseUrl: "",
        isValid: false,
        error: "Missing apiKey URL parameter",
      };
    }

    if (!apiKey.startsWith("pk_")) {
      return {
        venueId,
        apiKey,
        position: "bottom-right",
        welcomeDelay: 2500,
        baseUrl: "",
        isValid: false,
        error: "Invalid apiKey format. Expected format: pk_xxx",
      };
    }

    // Parse optional params
    const primaryColor = params.get("primaryColor") || undefined;
    const accentColor = params.get("accentColor") || undefined;
    const positionParam = params.get("position");
    const position: WidgetPosition =
      positionParam === "bottom-left" ? "bottom-left" : "bottom-right";
    const welcomeDelayParam = params.get("welcomeDelay");
    const welcomeDelay = welcomeDelayParam
      ? parseInt(welcomeDelayParam, 10)
      : 2500;
    const baseUrl = params.get("baseUrl") || "";

    return {
      venueId,
      apiKey,
      primaryColor,
      accentColor,
      position,
      welcomeDelay: isNaN(welcomeDelay) ? 2500 : welcomeDelay,
      baseUrl,
      isValid: true,
    };
  }, []);
}

/**
 * Convert config to widget props
 */
export function configToProps(config: UrlConfig): ConciergeWidgetProps | null {
  if (!config.isValid) {
    return null;
  }

  return {
    venueId: config.venueId,
    apiKey: config.apiKey,
    primaryColor: config.primaryColor,
    accentColor: config.accentColor,
    position: config.position,
    welcomeDelay: config.welcomeDelay,
    baseUrl: config.baseUrl,
  };
}
